import { useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import classNames from 'classnames';
import ClearIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { TOKEN_TYPE } from 'common/util/filter/types';
import { highlight } from 'common/util/filter/filter';

import ContentEditable from 'components/common/ContentEditable/ContentEditable';
import { draftSearchParamState } from 'components/common/Managers/RouteStateManager.state';
import SearchHelper from 'components/search/SearchHelper/SearchHelper';

import './FilterInput.scss';

type FilterInputProps = {
  onEnter:                 () => void;
  excludeTypes?:           TOKEN_TYPE[];
  crossTeamSearchEnabled?: boolean;
};

/**
 * A FilterInput is an input field which performs syntax highlighting as the user types a
 * search filter. It has a button to clear the input, and will run a search if the Enter
 * key is pressed.
 *
 * @prop onEnter               function to call if user types a return
 * @prop excludeTypes          token types to exclude from matching
 */
function FilterInput({
  onEnter,
  excludeTypes = [],
  crossTeamSearchEnabled = false,
}: FilterInputProps): JSX.Element {

  const [ draftParams, setDraftParams ] = useRecoilState(draftSearchParamState);
  const value = draftParams[K.URL_PARAM.FILTER] || '';
  const inputRef = useRef<HTMLDivElement>(null);
  const [ showSearchHelper, setShowSearchHelper ] = useState(false);

  // update draft params recoil state with a new value for filter
  const updateDraftFilter = ((filter: string): void => setDraftParams((state) => ({ ...state, [K.URL_PARAM.FILTER]: filter })));

  // update draft state as user types
  const handleChange = (inputText: string): void => {
    updateDraftFilter(inputText);

    // show the search helper if there is any filter text; the helper may not actually appear depending on the filter
    if (inputText) {
      setShowSearchHelper(true);
    }
  };

  const clearIconClass = classNames('FilterInput__ClearIcon', { hidden: !value });
  const inputClass = classNames('FilterInput__Input', { CrossTeam: crossTeamSearchEnabled });

  return (
    <SearchHelper
      showSearchHelper={showSearchHelper}
      setShowSearchHelper={setShowSearchHelper}
      onChange={handleChange}
      onEnter={onEnter}
      inputRef={inputRef}
      excludeTypes={excludeTypes}
    >
      <div className="FilterInput">
        <SearchIcon className="FilterInput__SearchIcon" />
        <ContentEditable
          html={highlight(value)}
          className={inputClass}
          placeholder="Search"
          onChange={handleChange}
          inputRef={inputRef}
          testId={ELEMENT.searchFilterInput}
        />
        <div className={clearIconClass} data-test-id={ELEMENT.searchFilterInputClear}>
          <ClearIcon onClick={(): void => updateDraftFilter('')} />
        </div>
      </div>
    </SearchHelper>
  );
}

export default FilterInput;
